import { computeMealTotals, itemToGroceryDelta, formatItemLabel } from '../data/foods'
import { GROCERY_ITEMS } from '../data/groceries'
import { getEffectiveDayPlan } from '../data/plan'

// Merges a plan's default meal with a saved override (if any). An override
// is { items: [{ foodKey, qty }] } - fully replaces that meal's contents for
// this specific day only; the base menu rotation is untouched.
export function getEffectiveMeal(planMeal, override) {
  if (!override?.items?.length) return { ...planMeal, isCustom: false }
  const totals = computeMealTotals(override.items)
  return {
    ...planMeal,
    ...totals,
    isCustom: true,
    customItems: override.items,
    items: override.items.map((i) => formatItemLabel(i)),
  }
}

export function getEffectiveMeals(planMeals, customMeals) {
  return planMeals.map((m) => getEffectiveMeal(m, customMeals?.[m.key]))
}

// Grocery deductions for one effective meal - from its custom items if
// edited, otherwise the plan's own ingredient list.
export function getEffectiveIngredients(effectiveMeal) {
  if (effectiveMeal.isCustom) {
    return (effectiveMeal.customItems || []).map(itemToGroceryDelta).filter(Boolean)
  }
  return effectiveMeal.ingredients || []
}

// Total ingredients needed across a whole day's effective meals (for the
// shopping list) - same shape as getIngredientsForMenu but override-aware.
export function getEffectiveDayIngredients(effectiveMeals) {
  const totals = {}
  for (const meal of effectiveMeals) {
    for (const { key, qty } of getEffectiveIngredients(meal)) {
      totals[key] = (totals[key] || 0) + qty
    }
  }
  return totals
}

// Same as getEffectiveDayIngredients but shaped like getIngredientsForMenu's
// array output (with name/category/unit attached), for the Groceries page.
export function getEffectiveGroceryList(effectiveMeals) {
  const totals = getEffectiveDayIngredients(effectiveMeals)
  return Object.entries(totals).map(([key, qty]) => ({ key, qty, ...GROCERY_ITEMS[key] }))
}

// Items to seed the meal editor with: the saved override if this meal has
// one, otherwise the plan's own ingredients converted to {foodKey, qty}
// (grocery keys line up 1:1 with food keys for every plan ingredient).
export function getMealEditableItems(effectiveMeal) {
  if (effectiveMeal.isCustom) return effectiveMeal.customItems || []
  return (effectiveMeal.ingredients || []).map(({ key, qty }) => ({ foodKey: key, qty }))
}

// Kcal/protein actually eaten (checked meals) on a given day, recomputed
// from the stored log rather than persisted separately - for the Progress
// page's nutrition trend chart.
export function computeDayNutrition(dayNumber, log, aiPlan) {
  const plan = getEffectiveDayPlan(dayNumber, aiPlan)
  const effectiveMeals = getEffectiveMeals(plan.meals, log?.customMeals)
  const checked = effectiveMeals.filter((m) => log?.mealChecks?.[m.key])
  return {
    checkedKcal: checked.reduce((s, m) => s + m.kcal, 0),
    checkedProtein: Math.round(checked.reduce((s, m) => s + m.protein, 0) * 10) / 10,
  }
}
